import { Component, Input, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { AssetsService } from '../../../core/services/assets.service';
import { AutocompleteOption } from '../autocomplete-select/autocomplete-select.component';

@Component({
  selector: 'app-timezone-select',
  standalone: false,
  templateUrl: './timezone-select.html',
  styleUrl: './timezone-select.scss',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TimezoneSelect),
    multi: true,
  }],
})
export class TimezoneSelect implements ControlValueAccessor, OnInit {
  @Input() placeholder = 'Select timezone';
  @Input() selectClass = 'form-select';

  options: AutocompleteOption[] = [];
  value = '';
  disabled = false;

  readonly search = (query: string) => this.load(query);
  readonly preload = (values: string[]) => this.preloadValues(values);

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(private readonly assets: AssetsService) {}

  ngOnInit(): void {
    setTimeout(() => void this.load(''), 0);
  }

  writeValue(value: unknown): void {
    this.value = typeof value === 'string' ? value : '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  valueChanged(value: string | string[]): void {
    this.value = Array.isArray(value) ? String(value[0] ?? '') : value;
    this.onChange(this.value);
    this.onTouched();
  }

  private async load(query: string): Promise<AutocompleteOption[]> {
    const loaded = (await this.assets.getTimezones(query)).map(toOption);
    this.options = mergeOptions(this.options, loaded);
    return loaded;
  }

  private async preloadValues(values: string[]): Promise<AutocompleteOption[]> {
    const known = new Set(await this.assets.getTimezones());
    const loaded = values.filter((value) => known.has(value)).map(toOption);
    this.options = mergeOptions(this.options, loaded);
    return loaded;
  }
}

function toOption(timezone: string): AutocompleteOption {
  return { value: timezone, label: timezone.replaceAll('_', ' ') };
}

function mergeOptions(
  current: AutocompleteOption[],
  incoming: AutocompleteOption[],
): AutocompleteOption[] {
  return [...new Map([...current, ...incoming].map((option) => [option.value, option])).values()];
}
